<template lang="pug">
div
  div.d-flex.justify-center.mt-16(v-if="isFetching")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

  template(v-else)
    v-row(no-gutters :style="`min-height: ${viewHeight};`")
      v-col.px-8.shrink.white
        div.sticky(style="width: 200px; top: 106px;")
          .text-md.font-semibold.px-6.py-2.round-md.mb-2.no-wrap(v-for="title in stepTitles" :class="stepTitles.indexOf(title) === step ? 'neutral200' : 'neutral500--text'") {{ title }}

      v-col.grow.neutral200
        v-container.pa-8(fluid style="max-width:1200px")
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="goBack()")
                v-icon(color="basic800" :size="24") mdi-arrow-left
            v-col.ml-3
              .text-2xl.font-semibold.neutral700--text Create an offer campaign:&nbsp;
                span.font-medium {{ stepTitles[step] }}

          v-window(v-model="step" touchless)
            v-window-item(:value="0")
              offer-details(ref="offerDetails" @updated="updateSetOffers($event)" @error="isLoading = false" @quit="goToOffers")
            v-window-item(:value="1")
              audience(ref="audience" @updated="updateSetOffers($event)" @error="isLoading = false" @quit="goToOffers")
            v-window-item(:value="2")
              delivery-methods(ref="deliveryMethods" @updated="updateBlastOffers" @error="isLoading = false" @quit="goToOffers")
            v-window-item(:value="3")
              thankyou-message(ref="thankYouMessage" @updated="updateBlastOffers" @error="isLoading = false" @quit="goToOffers")
            v-window-item(:value="4")
              offer-setup-review


          v-footer.white.py-3.px-10(app inset padless elevation="3")
            v-spacer
            y-button.mr-4(:label="$t('exit')" underline color="neutral700" size="md" button-class="px-0" @click="openQuitDialog = true")
            y-button.mr-4(:label="$t('back')" color="neutral700" size="md" outlined :disabled="step === 0" @click="goBack()")
            y-button(:label="step !== stepTitles.length - 1 ? 'Next' : 'Create'" color="neutral700" dark :loading="isLoading" @click="saveData")

  .dialog
    quit-create-offer-dialog(v-if="openQuitDialog" :open.sync="openQuitDialog" :offer-id="id" @savequit="saveAndQuit" @discardquit="goToOffers")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OfferDetails from '@/components/clinic/marketing/OfferDetails.vue'
import OfferSetupReview from '@/components/clinic/marketing/OfferSetupReview.vue'
import QuitCreateOfferDialog from '@/components/clinic/marketing/dialogs/QuitCreateOfferDialog.vue'
import Audience from "@/components/clinic/marketing/Audience.vue"
import DeliveryMethods from "@/components/clinic/marketing/DeliveryMethods.vue";
import ThankyouMessage from "@/components/clinic/marketing/ThankyouMessage.vue";

export default {
  components: {
    ThankyouMessage, DeliveryMethods, Audience, OfferDetails, OfferSetupReview, QuitCreateOfferDialog
  },
  props: ['id'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      openQuitDialog: false,
      stepTitles: ['Offer details', 'Audience', 'Delivery methods', 'Thank you message', 'Review']
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      storedStep: 'marketing/getCreateOfferStep',
      offer: 'marketing/getOffer',
      displaySize: 'ui/getDisplaySize'
    }),
    step: {
      get () {
        return this.storedStep
      },
      set (val) {
        this.$store.commit('marketing/setCreateOfferStep', val)
      }
    },
    viewHeight () {
      switch (this.displaySize) {
        case 'xx-small':
          return '130vh'
        case 'x-small':
          return '114vh'
        case 'small':
          return '106vh'
        case 'medium':
          return '92vh'
        case 'large':
          return '81vh'
        case 'x-large':
          return '81vh'
        default:
          return '81vh'
      }
    }
  },
  mounted () {
    this.fetchSettings()
    this.fetchLoyaltyMembers()
    this.fetchCustomers()
    if (this.id) {
      this.fetchOffers()
      this.step = this.stepTitles.length - 1
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchOffers (showLoadingState = true) {
      if (showLoadingState) this.isFetching = true
      let params = { expand: 'purchase_products,free_products' }
      this.$axios.get(`/marketing/${this.company.slug}/offers/${this.id}`, params)
        .then(resp => {
          this.$store.commit('marketing/setOffer', resp.data)
          this.fetchOfferBlast(resp.data.id)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchOfferBlast (id) {
      return this.$axios.get(`/marketing/${this.company.slug}/offers/${id}/blasts`)
        .then(resp => {
          if (resp.data.results.length) {
            this.$store.commit('marketing/setBlasts', resp.data.results)
          }
        })
        .catch(() => {})
    },
    fetchSettings () {
      this.$axios.get(`/marketing/${this.company.slug}`)
        .then(resp => {
          this.$store.commit('marketing/setMarketingSettings', resp.data)
        })
        .catch(() => {})
    },
    fetchLoyaltyMembers () {
      this.$axios.get(`/loyalty/${this.company.slug}/members?page_size=1`)
        .then(({ data }) => {
          this.$store.commit('marketing/setTotalLoyaltyMemberSummary', data.count)
        })
        .catch(() => {})
    },
    fetchCustomers () {
      this.$axios.get(`/marketing/${this.company.slug}/customers?page_size=1`)
        .then(({ data }) => {
          this.$store.commit('marketing/setTotalCustomerSummary', data.count)
        })
        .catch(() => {})
    },
    updateSetOffers (id) {
      this.isLoading = false
      this.step++
      if (!this.id) {
        this.$router.push(`/admin/clinic/marketing/offer/create/${id}`)
      }
    },
    updateBlastOffers () {
      if (this.step === 3) {
        this.fetchOffers(false)
      }
      this.isLoading = false
      this.step++
    },
    async saveData () {
      if (this.step === 0) {
        this.isLoading = true
        await this.$refs.offerDetails.saveData()
        return
      }
      if (this.step === 1) {
        this.isLoading = true
        await this.$refs.audience.saveData()
        return
      }
      if (this.step === 2) {
        this.isLoading = true
        await this.$refs.deliveryMethods.saveData()
        return
      }
      if (this.step === 3) {
        this.isLoading = true
        await this.$refs.thankYouMessage.saveData()
        return
      }
      if (this.step === 4) {
        this.isLoading = true
        this.$axios.patch(`/marketing/${this.company.slug}/offers/${this.offer.id}`, { status: 'scheduled' })
          .then(() => {
            this.goToOffers()
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
            this.$emit('error')
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    async saveAndQuit () {
      this.isLoading = true
      if (this.step === 0) {
        await this.$refs.offerDetails.saveData('quit')
        return
      }
      if (this.step === 1) {
        await this.$refs.audience.saveData('quit')
        return
      }
      if (this.step === 2) {
        await this.$refs.deliveryMethods.saveData('quit')
        return
      }
      if (this.step === 3) {
        await this.$refs.thankYouMessage.saveData('quit')
        return
      }
      this.goToOffers()
    },
    goBack () {
      if (this.step === 0) {
        this.openQuitDialog = true
      } else {
        this.step--
      }
    },
    goToOffers () {
      this.$router.push('/admin/clinic/marketing')
    },
  },
  beforeDestroy () {
    this.$store.commit('marketing/resetMarketingOffer')
  }
}
</script>

<style scoped>
</style>